import StarterKit from '@tiptap/starter-kit';
import { Markdown, type MarkdownStorage } from 'tiptap-markdown';
import type { Editor, Extensions } from '@tiptap/core';

// tiptap-markdown doesn't augment TipTap v3's per-extension `Storage` map, so
// `editor.storage.markdown` is untyped without this.
declare module '@tiptap/core' {
  interface Storage {
    markdown: MarkdownStorage;
  }
}

/**
 * The web half of the binder-document Markdown contract
 * (docs/architecture/07-client-contract.md §3.7.1.x). A document `body` is a
 * plain string carrying a small CommonMark subset — **bold**, *italic*,
 * bulleted (`-`) and numbered (`1.`) lists — that round-trips byte-for-byte
 * across web, iOS, and Android. This module is the single place the web editor
 * (TipTap) and renderer (react-markdown) agree on that subset.
 *
 * The editor may only ever PRODUCE these constructs; the renderer only RENDERS
 * these tags. Everything else (headings, code, blockquote, links, raw HTML) is
 * disabled here so a body can neither be authored with nor rendered as it.
 */

/** The only HTML elements react-markdown is allowed to emit for a document body. */
export const DOC_MARKDOWN_ALLOWED_ELEMENTS = ['p', 'strong', 'em', 'ul', 'ol', 'li'];

/**
 * The constrained TipTap extension set: StarterKit trimmed to paragraph, bold,
 * italic, bullet/ordered lists (+ undo/redo), with the tiptap-markdown extension
 * configured for the contract's normal form. Heading/code/blockquote/strike/
 * horizontalRule — and link/underline, which StarterKit v3 bundles — are
 * disabled so the editor cannot represent them.
 */
export const DOC_EXTENSIONS: Extensions = [
  StarterKit.configure({
    heading: false,
    code: false,
    codeBlock: false,
    blockquote: false,
    horizontalRule: false,
    strike: false,
    link: false,
    underline: false,
  }),
  Markdown.configure({
    html: false,
    bulletListMarker: '-',
    linkify: false,
    breaks: false,
    transformPastedText: true,
    transformCopiedText: false,
  }),
];

/**
 * Serialize the editor's document to normal-form Markdown for storage in
 * `VaultItem.body`. Trailing whitespace/newlines are trimmed so the output is
 * byte-stable across round-trips.
 */
export function serializeDocMarkdown(editor: Editor): string {
  const md: string = editor.storage.markdown.getMarkdown();
  return md.replace(/[\s﻿]+$/, '');
}

/**
 * Cross-client round-trip fixture — the canonical document body exercising all
 * four constructs plus nesting. Every client asserts `parse → serialize`
 * returns this string unchanged (the crypto byte-parity discipline applied to
 * text). Keep this identical to the iOS/Android fixtures.
 */
export const CANONICAL_DOC_FIXTURE = [
  'This note has **bold** and *italic* words.',
  '',
  '- first bullet',
  '- second bullet',
  '',
  '1. first step',
  '2. second step',
].join('\n');
